import { useState, useEffect, useCallback, useMemo } from 'react';
import { subDays, previousFriday } from 'date-fns';
import { toLocalDateStr } from '../lib/utils';
import { JiraIssueDetail } from './JiraIssueDetail';
import { JiraLagIndicators } from './JiraLagIndicators';
import { getStoredGitAliases, resolveGitAuthor } from './SettingsView';
import * as api from '@/services/api';
import { getStatusColor, getStatusSortOrder } from '@/services/jira-status-config';
import { WaterfallView, type WaterfallEvent } from './WaterfallView';
import { generateAndDownloadReport } from '@/services/api';
import { notifyAnalysisComplete } from '@/services/notifications';
import { NotificationPermissionDialog } from './NotificationPermissionDialog';
import * as historyStore from '@/services/analysis-history-store';
import * as audit from '@/services/audit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRightArrowLeft,
  faArrowTrendUp,
  faBolt,
  faBuilding,
  faCalendarDays,
  faChartBar,
  faChartPie,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCodeBranch,
  faComment,
  faDownload,
  faEye,
  faEyeSlash,
  faFilter,
  faFlag,
  faLayerGroup,
  faMagnifyingGlass,
  faPlus,
  faRotate,
  faSpinner,
  faUserPen,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

// Types
interface ActivityEvent {
  id: string;
  timestamp: string;
  type: "status_change" | "assignment" | "comment" | "sprint_change" | "priority_change" | "created" | "resolved" | "field_change";
  issueKey: string;
  issueSummary: string;
  issueType: string;
  issueTypeIcon?: string;
  isSubtask?: boolean;
  parent?: { key: string; summary: string; issueType?: string };
  labels?: string[];
  priority?: string;
  priorityIcon?: string;
  author: {
    displayName: string;
    accountId: string;
    avatarUrl?: string;
  };
  fromStatus?: string;
  toStatus?: string;
  fromAssignee?: string;
  toAssignee?: string;
  fromSprint?: string;
  toSprint?: string;
  fieldName?: string;
  fromValue?: string;
  toValue?: string;
  commentBody?: string;
}

interface ActivityFeed {
  events: ActivityEvent[];
  totalEvents: number;
  developers: { displayName: string; accountId: string; avatarUrl?: string; issueCount: number }[];
  statusBreakdown: { status: string; count: number }[];
  issueTypeBreakdown: { type: string; count: number }[];
  sprintSummary: {
    name: string;
    state: string;
    totalIssues: number;
    completedIssues: number;
    startDate?: string;
    endDate?: string;
  }[];
  dateRange: { start: string; end: string };
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraReviewData {
  feed: any; // ActivityFeed
  selectedProject: string;
  customFrom: string;
  customTo: string;
}

interface JiraActivityViewProps {
  onClose: () => void;
  jiraDomain: string;
  jiraEmail: string;
  jiraApiToken: string;
  defaultProjectKey?: string;
  defaultBoardId?: number;
  embedded?: boolean;
  inline?: boolean;
  showLagIndicators?: boolean;
  priorityUsers?: string[];
  reviewData?: JiraReviewData;
}

const EVENT_TYPE_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  status_change: { icon: mkIcon(faArrowRightArrowLeft), color: "text-blue-400", label: "Status Change" },
  assignment: { icon: mkIcon(faUserPen), color: "text-purple-400", label: "Assignment" },
  sprint_change: { icon: mkIcon(faCodeBranch), color: "text-cyan-400", label: "Sprint Change" },
  priority_change: { icon: mkIcon(faFlag), color: "text-orange-400", label: "Priority Change" },
  created: { icon: mkIcon(faPlus), color: "text-emerald-700 dark:text-emerald-400", label: "Created" },
  resolved: { icon: mkIcon(faCircleCheck), color: "text-green-700 dark:text-green-400", label: "Resolved" },
  field_change: { icon: mkIcon(faBolt), color: "text-yellow-400", label: "Field Change" },
  comment: { icon: mkIcon(faComment), color: "text-slate-400", label: "Comment" },
};

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}


// getStatusColor imported from @/services/jira-status-config

export function JiraActivityView({ onClose, jiraDomain, jiraEmail, jiraApiToken, defaultProjectKey, defaultBoardId, embedded, inline, showLagIndicators, priorityUsers, reviewData }: JiraActivityViewProps) {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [lagTrigger, setLagTrigger] = useState(0);
  const [isReview, setIsReview] = useState(false);

  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [selectedProject, setSelectedProject] = useState(defaultProjectKey || '');
  const [dateRangePreset, setDateRangePreset] = useState<number | 'friday-monday'>(30);
  const [customFrom, setCustomFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return toLocalDateStr(d);
  });
  const [customTo, setCustomTo] = useState<string>(toLocalDateStr());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const lookbackDays = Math.max(1, Math.ceil((new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000));
  const [feed, setFeed] = useState<ActivityFeed | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterAuthor, setFilterAuthor] = useState<string>('');
  const [filterType, _setFilterType] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterIssueType, setFilterIssueType] = useState<string>('');
  const [filterSprint, setFilterSprint] = useState<string>('');
  const [filterLabel, setFilterLabel] = useState<string>('');
  const [showAssignments, setShowAssignments] = useState(true);
  const [showStatusChanges, setShowStatusChanges] = useState(true);
  const [showCreated, setShowCreated] = useState(true);
  const [showSprintChanges, setShowSprintChanges] = useState(true);
  const [showComments, setShowComments] = useState(true);
  const [_hideMergeEvents, _setHideMergeEvents] = useState(false);
  const [groupByTicket, setGroupByTicket] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(new Set());
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Hydrate from reviewData (past analysis reopen)
  useEffect(() => {
    if (!reviewData) return;
    setFeed(reviewData.feed);
    setSelectedProject(reviewData.selectedProject);
    setCustomFrom(reviewData.customFrom);
    setCustomTo(reviewData.customTo);
    setIsReview(true);
    const today = toLocalDateStr();
    const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
    setExpandedDates(new Set([today, yesterday]));
  }, [reviewData]);

  useEffect(() => {
    if (reviewData) return;
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      let data: any;
      data = await api.getJiraProjects(jiraDomain, jiraEmail, jiraApiToken);
      if (Array.isArray(data)) setProjects(data);
      else if (data?.projects) setProjects(data.projects);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchActivity = useCallback(async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    setLoading(true);
    setError(null);
    const startTime = Date.now();
    audit.auditAnalysisStarted('jira', { project: selectedProject });
    try {
      let data: any;
      data = await api.getJiraActivity(
        jiraDomain, jiraEmail, jiraApiToken, selectedProject, lookbackDays, defaultBoardId, customFrom, customTo
      );
      if (data.error) {
        setError(data.error);
        notifyAnalysisComplete({ mode: 'jira', commitCount: 0, eventCount: 0, durationMs: Date.now() - startTime, success: false, error: data.error });
      } else {
        setFeed(data);
        if (showLagIndicators) setLagTrigger(prev => prev + 1);
        const today = toLocalDateStr();
        const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
        setExpandedDates(new Set([today, yesterday]));
        const durationMs = Date.now() - startTime;
        audit.auditAnalysisCompleted('jira', durationMs, { project: selectedProject, eventCount: data.events?.length || 0 });
        notifyAnalysisComplete({ mode: 'jira', commitCount: 0, eventCount: data.events?.length || 0, durationMs, success: true });

        // Save to analysis history
        const snapId = historyStore.generateSnapshotId();
        historyStore.saveSnapshot(
          {
            id: snapId,
            mode: 'jira',
            title: `Jira — ${selectedProject}`,
            timestamp: new Date().toISOString(),
            durationMs,
            success: true,
            summary: {
              commitCount: 0,
              eventCount: data.events?.length || 0,
              authorCount: 0,
              dateRange: { start: customFrom, end: customTo },
            },
          },
          { feed: data, selectedProject, customFrom, customTo } satisfies JiraReviewData
        ).catch(err => console.error('[analysis-history] Failed to save jira snapshot:', err));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch activity');
      audit.auditAnalysisFailed('jira', err.message, { project: selectedProject });
      notifyAnalysisComplete({ mode: 'jira', commitCount: 0, eventCount: 0, durationMs: Date.now() - startTime, success: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }, [selectedProject, customFrom, customTo, jiraDomain, jiraEmail, jiraApiToken, defaultBoardId]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    if (!feed) return [];
    return feed.events.filter((e) => {
      if (filterAuthor && e.author.displayName !== filterAuthor) return false;
      if (filterType && e.type !== filterType) return false;
      if (!showAssignments && e.type === 'assignment') return false;
      if (!showStatusChanges && e.type === 'status_change') return false;
      if (!showCreated && e.type === 'created') return false;
      if (!showSprintChanges && e.type === 'sprint_change') return false;
      if (!showComments && e.type === 'comment') return false;
      if (filterStatus) {
        const hasStatus = (e.type === 'status_change' && e.toStatus === filterStatus) ||
          (e.type === 'created' && feed.events.some(ev => ev.issueKey === e.issueKey && ev.type === 'status_change' && ev.toStatus === filterStatus));
        // For non-status-change events, check if the issue's latest status matches
        if (e.type === 'status_change' && e.toStatus !== filterStatus) return false;
        if (e.type !== 'status_change' && !hasStatus) {
          // Check if any status_change event for this issue has the target status
          const issueHasStatus = feed.events.some(ev => ev.issueKey === e.issueKey && ev.type === 'status_change' && ev.toStatus === filterStatus);
          if (!issueHasStatus) return false;
        }
      }
      if (filterIssueType && e.issueType !== filterIssueType) return false;
      if (filterSprint) {
        const matchesSprint = (e.type === 'sprint_change' && e.toSprint === filterSprint) ||
          feed.events.some(ev => ev.issueKey === e.issueKey && ev.type === 'sprint_change' && ev.toSprint === filterSprint);
        if (!matchesSprint) return false;
      }
      if (filterLabel) {
        if (!e.labels || !e.labels.includes(filterLabel)) return false;
      }
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!e.issueKey.toLowerCase().includes(q) &&
          !e.issueSummary.toLowerCase().includes(q) &&
          !e.author.displayName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [feed, filterAuthor, filterType, filterSearch, filterStatus, filterIssueType, filterSprint, filterLabel, showAssignments, showStatusChanges, showCreated, showSprintChanges, showComments]);

  // Group events by date
  const groupedByDate = useMemo(() => {
    const groups = new Map<string, ActivityEvent[]>();
    for (const event of filteredEvents) {
      const date = toLocalDateStr(new Date(event.timestamp));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(event);
    }
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredEvents]);

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const toggleTicket = (key: string) => {
    setExpandedTickets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Group by ticket: collapse all events per issueKey into one row
  interface TicketGroup {
    issueKey: string;
    issueSummary: string;
    issueType: string;
    issueTypeIcon?: string;
    priority?: string;
    priorityIcon?: string;
    parent?: { key: string; summary: string; issueType?: string };
    latestStatus?: string;
    events: ActivityEvent[];
    latestTimestamp: string;
  }

  const ticketGroups = useMemo((): TicketGroup[] => {
    if (!groupByTicket) return [];
    const map = new Map<string, TicketGroup>();
    for (const event of filteredEvents) {
      if (!map.has(event.issueKey)) {
        map.set(event.issueKey, {
          issueKey: event.issueKey,
          issueSummary: event.issueSummary,
          issueType: event.issueType,
          issueTypeIcon: event.issueTypeIcon,
          priority: event.priority,
          priorityIcon: event.priorityIcon,
          parent: event.parent,
          events: [],
          latestTimestamp: event.timestamp,
        });
      }
      const group = map.get(event.issueKey)!;
      group.events.push(event);
      if (new Date(event.timestamp) > new Date(group.latestTimestamp)) {
        group.latestTimestamp = event.timestamp;
      }
      // Track latest status
      if (event.type === 'status_change' && event.toStatus) {
        group.latestStatus = event.toStatus;
      }
    }
    return Array.from(map.values()).sort((a, b) => new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime());
  }, [filteredEvents, groupByTicket]);

  // ==================== CHART DATA ====================
  const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4', '#f97316', '#ec4899', '#6366f1', '#14b8a6'];

  const jiraDailyData = useMemo(() => {
    if (!feed) return [];
    const dayMap = new Map<string, { date: string; _ts: number; events: number }>();
    for (const e of feed.events) {
      const d = new Date(e.timestamp);
      const key = toLocalDateStr(d);
      if (!dayMap.has(key)) dayMap.set(key, { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), _ts: d.setHours(0, 0, 0, 0), events: 0 });
      dayMap.get(key)!.events++;
    }
    return Array.from(dayMap.values()).sort((a, b) => a._ts - b._ts).map(({ _ts, ...rest }) => rest);
  }, [feed]);

  const authorChartData = useMemo(() => {
    if (!feed) return [];
    return feed.developers
      .map(d => ({ name: d.displayName.length > 12 ? d.displayName.split(' ')[0] : d.displayName, fullName: d.displayName, issues: d.issueCount }))
      .sort((a, b) => b.issues - a.issues);
  }, [feed]);

  const eventTypeChartData = useMemo(() => {
    if (!feed) return [];
    const counts = new Map<string, number>();
    for (const e of feed.events) { counts.set(e.type, (counts.get(e.type) || 0) + 1); }
    return Array.from(counts.entries())
      .map(([type, count], i) => ({ name: EVENT_TYPE_CONFIG[type]?.label || type, count, fill: CHART_COLORS[i % CHART_COLORS.length] }))
      .sort((a, b) => b.count - a.count);
  }, [feed]);

  const jiraStatusChartData = useMemo(() => {
    if (!feed) return [];
    return feed.statusBreakdown.slice(0, 10).map((s, i) => ({ name: s.status, count: s.count, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  }, [feed]);

  const jiraIssueTypeChartData = useMemo(() => {
    if (!feed) return [];
    return feed.issueTypeBreakdown.map((t, i) => ({ name: t.type, count: t.count, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  }, [feed]);

  const sprintChartData = useMemo(() => {
    if (!feed || feed.sprintSummary.length === 0) return [];
    return feed.sprintSummary
      .filter(s => s.totalIssues > 0)
      .slice(-6)
      .map(s => ({
        name: s.name.length > 15 ? s.name.substring(0, 13) + '..' : s.name,
        fullName: s.name,
        completed: s.completedIssues,
        remaining: s.totalIssues - s.completedIssues,
        total: s.totalIssues,
      }));
  }, [feed]);

  const jiraWaterfallEvents: WaterfallEvent[] = useMemo(() => {
    if (!feed) return [];
    return feed.events.map(e => ({
      id: e.id,
      timestamp: e.timestamp,
      issueKey: e.issueKey,
      issueSummary: e.issueSummary,
      type: e.type,
      author: e.author.displayName,
      platform: 'jira' as const,
      issueType: e.issueType,
      detail: e.fromStatus && e.toStatus ? `${e.fromStatus} → ${e.toStatus}` : e.toAssignee ? `→ ${e.toAssignee}` : e.commentBody ? `"${e.commentBody.substring(0, 60)}"` : undefined,
    }));
  }, [feed]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const renderEventContent = (event: ActivityEvent) => {
    switch (event.type) {
      case 'status_change':
        return (
          <div className="flex items-center gap-2 flex-wrap">
            {event.fromStatus && (
              <span className={`px-2 py-0.5 text-[11px] rounded border ${getStatusColor(event.fromStatus)}`}>{event.fromStatus}</span>
            )}
            <span className="text-slate-500">→</span>
            {event.toStatus && (
              <span className={`px-2 py-0.5 text-[11px] rounded border ${getStatusColor(event.toStatus)}`}>{event.toStatus}</span>
            )}
          </div>
        );
      case 'assignment':
        return (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">{event.fromAssignee || 'Unassigned'}</span>
            <span className="text-slate-500">→</span>
            <span className="text-purple-300 font-medium">{event.toAssignee || 'Unassigned'}</span>
          </div>
        );
      case 'sprint_change':
        return (
          <div className="flex items-center gap-2 text-xs">
            {event.fromSprint && <span className="text-slate-400">{event.fromSprint}</span>}
            {event.fromSprint && <span className="text-slate-500">→</span>}
            <span className="text-cyan-300 font-medium">{event.toSprint || 'Removed from sprint'}</span>
          </div>
        );
      case 'priority_change':
        return (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">{event.fromValue}</span>
            <span className="text-slate-500">→</span>
            <span className="text-orange-300 font-medium">{event.toValue}</span>
          </div>
        );
      case 'created':
        return <span className="text-xs text-emerald-700 dark:text-emerald-400">Issue created</span>;
      case 'resolved':
        return <span className="text-xs text-green-700 dark:text-green-400">Resolved: {event.toValue}</span>;
      case 'comment':
        return (
          <div className="text-xs text-gray-600 dark:text-slate-400 italic max-w-md truncate">
            {event.commentBody ? `"${event.commentBody}"` : 'Added a comment'}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={embedded ? 'h-full overflow-y-auto' : inline ? 'min-h-full overflow-y-auto' : 'fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm overflow-y-auto'}>
      <NotificationPermissionDialog isDark={isDark} />
      {isReview && (
        <div className={`px-4 py-2 text-xs font-medium text-center ${isDark ? 'bg-amber-500/10 text-amber-300 border-b border-amber-500/20' : 'bg-amber-50 text-amber-700 border-b border-amber-200'}`}>
          Reviewing past analysis — data is read-only
        </div>
      )}
      {/* Header - hidden when inline (uses main app header) */}
      {!inline && (
        <div className={`sticky top-0 z-10 ${embedded ? 'bg-slate-900/90' : 'bg-white/80 dark:bg-slate-900/80'} backdrop-blur-md border-b border-gray-200 dark:border-slate-700/50`}>
          <div className={`${embedded ? 'px-4 py-3' : 'max-w-7xl mx-auto px-6 py-4'} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              {!embedded && (
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                </button>
              )}
              <FontAwesomeIcon icon={faLayerGroup} className={`${embedded ? 'h-4 w-4' : 'h-5 w-5'} text-blue-400`} />
              <div>
                <h1 className={`${embedded ? 'text-sm' : 'text-lg'} font-semibold text-gray-900 dark:text-white`}>{showLagIndicators ? 'Jira Mode' : 'Jira Activity'}</h1>
                <p className="text-xs text-gray-500 dark:text-slate-400">{showLagIndicators ? 'Activity timeline & lag indicators' : 'Full timeline of all Jira changes'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {feed && <span className="text-xs text-gray-400 dark:text-slate-500">{filteredEvents.length} events</span>}
              {feed && (
                <button
                  disabled={pdfDownloading}
                  onClick={async () => {
                    if (!feed) return;
                    setPdfDownloading(true);
                    try {
                      const fromDate = new Date(customFrom);
                      const toDate = new Date(customTo);
                      const projectObj = projects.find(p => p.key === selectedProject);

                      // Apply alias resolution to developer names
                      const jiraAliases = getStoredGitAliases();
                      const mergedDevMap = new Map<string, { displayName: string; issueCount: number }>();
                      for (const dev of feed.developers) {
                        const canonical = resolveGitAuthor(dev.displayName, jiraAliases);
                        if (mergedDevMap.has(canonical)) {
                          mergedDevMap.get(canonical)!.issueCount += dev.issueCount;
                        } else {
                          mergedDevMap.set(canonical, { displayName: canonical, issueCount: dev.issueCount });
                        }
                      }
                      const resolvedDevelopers = Array.from(mergedDevMap.values());

                      generateAndDownloadReport('jira', {
                        generatedAt: new Date().toISOString(),
                        dateRange: { start: fromDate.toISOString(), end: toDate.toISOString() },
                        lookbackDays,
                        projectKey: selectedProject,
                        projectName: projectObj?.name,
                        summary: {
                          totalEvents: feed.totalEvents,
                          totalDevelopers: resolvedDevelopers.length,
                          statusBreakdown: feed.statusBreakdown,
                          issueTypeBreakdown: feed.issueTypeBreakdown,
                          sprintSummary: feed.sprintSummary,
                          developers: resolvedDevelopers,
                        },
                        lagIndicators: [],
                        leadIndicators: [],
                        aggregateIndicators: [],
                        recentEvents: feed.events.slice(0, 50).map(e => ({
                          timestamp: e.timestamp,
                          type: e.type,
                          issueKey: e.issueKey,
                          issueSummary: e.issueSummary,
                          issueType: e.issueType,
                          author: resolveGitAuthor(e.author.displayName, jiraAliases),
                          detail: e.type === 'status_change' ? `${e.fromStatus || '?'} → ${e.toStatus || '?'}` :
                            e.type === 'assignment' ? `→ ${e.toAssignee || 'Unassigned'}` :
                              e.type === 'sprint_change' ? `→ ${e.toSprint || 'Removed'}` :
                                e.type === 'comment' ? (e.commentBody ? `"${e.commentBody.substring(0, 80)}"` : 'Comment added') : undefined,
                        })),
                      }, `verigit-jira-report-${selectedProject}-${Date.now()}.pdf`);
                    } catch (err) {
                      console.error('Failed to download Jira PDF:', err);
                    } finally {
                      setPdfDownloading(false);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 transition-colors"
                >
                  {pdfDownloading ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />}
                  <span className="text-xs">{pdfDownloading ? 'Generating...' : 'PDF Report'}</span>
                </button>
              )}
              {!embedded && (
                <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                  <FontAwesomeIcon icon={faXmark} className="h-5 w-5 text-slate-400" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Config Panel */}
        <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faBuilding} className="h-3.5 w-3.5" /> Project
              </label>
              {loadingProjects ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500 py-2.5"><FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading...</div>
              ) : (
                <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all">
                  <option value="">Select project...</option>
                  {projects.map((p) => <option key={p.key} value={p.key}>{p.name} ({p.key})</option>)}
                </select>
              )}
            </div>
            <div className="md:col-span-4">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                  <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5" /> Time Range
                </label>
                <button
                  onClick={() => setShowDatePicker(v => !v)}
                  className={`text-[10px] px-2 py-0.5 rounded transition-colors ${showDatePicker ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-400' : 'text-gray-500 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-300'
                    }`}
                >
                  {showDatePicker ? 'Hide Custom' : 'Custom Range'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  {
                    label: 'Friday -> Monday (now)',
                    getValue: () => {
                      const now = new Date();
                      return {
                        from: previousFriday(now),
                        to: now
                      };
                    }
                  },
                  { label: 'Yesterday', days: -1 },
                  { label: '24h', days: 1 },
                  { label: '7d', days: 7 },
                  { label: '2w', days: 14 },
                  { label: '30d', days: 30 },
                  { label: '60d', days: 60 },
                  { label: '90d', days: 90 },
                  { label: '6mo', days: 180 },
                  { label: 'All Time', days: -2 },
                ].map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setDateRangePreset(preset.getValue ? 'friday-monday' : (preset.days || 0));
                      let from: Date;
                      let to: Date;

                      if (preset.getValue) {
                        const value = preset.getValue();
                        from = value.from;
                        to = value.to;
                      } else if (preset.days === -1) {
                        from = subDays(new Date(), 1);
                        from.setHours(0, 0, 0, 0);
                        to = subDays(new Date(), 1);
                        to.setHours(23, 59, 59, 999);
                      } else if (preset.days === -2) {
                        from = new Date();
                        from.setFullYear(from.getFullYear() - 10);
                        to = new Date();
                      } else {
                        to = new Date();
                        from = subDays(to, preset.days);
                      }
                      setCustomFrom(toLocalDateStr(from));
                      setCustomTo(toLocalDateStr(to));
                      setShowDatePicker(false);
                    }}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${(preset.getValue ? dateRangePreset === 'friday-monday' : dateRangePreset === preset.days) && !showDatePicker
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'bg-gray-100 dark:bg-slate-700/30 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700/50 border border-transparent'
                      }`}>
                    {preset.label}
                  </button>
                ))}
              </div>
              {showDatePicker && (
                <div className="flex gap-2 items-center mb-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => { setCustomFrom(e.target.value); setDateRangePreset(0); }}
                    max={customTo}
                    className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                  <span className="text-xs text-gray-400 dark:text-slate-500">to</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => { setCustomTo(e.target.value); setDateRangePreset(0); }}
                    min={customFrom}
                    max={toLocalDateStr()}
                    className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
              )}
              <div className="text-[10px] text-slate-500">
                {new Date(customFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' — '}
                {new Date(customTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' '}({lookbackDays} days)
              </div>
            </div>
            <div className="md:col-span-4">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5" /> Search
              </label>
              <input type="text" value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Search issues..."
                className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all" />
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <button onClick={fetchActivity} disabled={loading || !selectedProject}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
                {loading ? (<><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Loading...</>) : (<><FontAwesomeIcon icon={faRotate} className="h-4 w-4" /> Fetch Activity</>)}
              </button>
              {feed && (
                <button
                  disabled={pdfDownloading}
                  onClick={async () => {
                    if (!feed) return;
                    setPdfDownloading(true);
                    try {
                      const fromDate = new Date(customFrom);
                      const toDate = new Date(customTo);
                      const projectObj = projects.find(p => p.key === selectedProject);
                      const jiraAliases = getStoredGitAliases();
                      const mergedDevMap = new Map<string, { displayName: string; issueCount: number }>();
                      for (const dev of feed.developers) {
                        const canonical = resolveGitAuthor(dev.displayName, jiraAliases);
                        if (mergedDevMap.has(canonical)) {
                          mergedDevMap.get(canonical)!.issueCount += dev.issueCount;
                        } else {
                          mergedDevMap.set(canonical, { displayName: canonical, issueCount: dev.issueCount });
                        }
                      }
                      const resolvedDevelopers = Array.from(mergedDevMap.values());
                      generateAndDownloadReport('jira', {
                        generatedAt: new Date().toISOString(),
                        dateRange: { start: fromDate.toISOString(), end: toDate.toISOString() },
                        lookbackDays,
                        projectKey: selectedProject,
                        projectName: projectObj?.name,
                        summary: {
                          totalEvents: feed.totalEvents,
                          totalDevelopers: resolvedDevelopers.length,
                          statusBreakdown: feed.statusBreakdown,
                          issueTypeBreakdown: feed.issueTypeBreakdown,
                          sprintSummary: feed.sprintSummary,
                          developers: resolvedDevelopers,
                        },
                        lagIndicators: [],
                        leadIndicators: [],
                        aggregateIndicators: [],
                        recentEvents: feed.events.slice(0, 50).map(e => ({
                          timestamp: e.timestamp,
                          type: e.type,
                          issueKey: e.issueKey,
                          issueSummary: e.issueSummary,
                          issueType: e.issueType,
                          author: resolveGitAuthor(e.author.displayName, jiraAliases),
                          detail: e.type === 'status_change' ? `${e.fromStatus || '?'} → ${e.toStatus || '?'}` :
                            e.type === 'assignment' ? `→ ${e.toAssignee || 'Unassigned'}` :
                              e.type === 'sprint_change' ? `→ ${e.toSprint || 'Removed'}` :
                                e.type === 'comment' ? (e.commentBody ? `"${e.commentBody.substring(0, 80)}"` : 'Comment added') : undefined,
                        })),
                      }, `verigit-jira-report-${selectedProject}-${Date.now()}.pdf`);
                    } catch (err) {
                      console.error('Failed to download Jira PDF:', err);
                    } finally {
                      setPdfDownloading(false);
                    }
                  }}
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  title="Download PDF Report"
                >
                  {pdfDownloading ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Lag Indicators (compact, embedded above the timeline) */}
        {showLagIndicators && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <JiraLagIndicators
              onClose={() => { }}
              jiraDomain={jiraDomain}
              jiraEmail={jiraEmail}
              jiraApiToken={jiraApiToken}
              defaultProjectKey={selectedProject || defaultProjectKey}
              priorityUsers={priorityUsers}
              embedded
              triggerCalculate={lagTrigger}
              parentLookbackDays={lookbackDays}
              parentStartDate={customFrom}
              parentEndDate={customTo}
              activityEvents={feed?.events}
              activitySprintSummary={feed?.sprintSummary}
            />
          </div>
        )}

        {/* Charts Section */}
        {feed && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowCharts(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Charts & Analytics</span>
                <span className="text-[10px] text-gray-400 dark:text-slate-500">({feed.totalEvents} events)</span>
              </div>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-slate-400 transition-transform ${showCharts ? 'rotate-180' : ''}`} />
            </button>
            {showCharts && (
              <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                {/* Daily Activity */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Daily Activity</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={180} minWidth={0}>
                    <AreaChart data={jiraDailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                      <Area type="monotone" dataKey="events" name="Events" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Events by Author */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5 text-purple-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Activity by Developer</h3>
                  </div>
                  <div className="overflow-y-auto max-h-[180px]">
                    <div style={{ height: Math.max(180, authorChartData.length * 32) }}>
                      <ResponsiveContainer width="100%" height={Math.max(180, authorChartData.length * 32)} minWidth={0}>
                        <BarChart data={authorChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                          <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} width={80} />
                          <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }}
                            labelFormatter={(label: string) => { const item = authorChartData.find(d => d.name === label); return item?.fullName || label; }} />
                          <Bar dataKey="issues" name="Issues Touched" fill="#a855f7" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Event Type Distribution */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faChartPie} className="h-3.5 w-3.5 text-amber-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Event Type Breakdown</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={180} minWidth={0}>
                    <PieChart>
                      <Pie data={eventTypeChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '9px' }}>
                        {eventTypeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Status Breakdown */}
                {jiraStatusChartData.length > 0 && (
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5 text-cyan-400" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Status Transitions</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180} minWidth={0}>
                      <BarChart data={jiraStatusChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} interval={0} angle={-30} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                        <Bar dataKey="count" name="Transitions" radius={[4, 4, 0, 0]}>
                          {jiraStatusChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Issue Type Distribution */}
                {jiraIssueTypeChartData.length > 0 && (
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faBolt} className="h-3.5 w-3.5 text-rose-400" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Issue Type Distribution</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180} minWidth={0}>
                      <PieChart>
                        <Pie data={jiraIssueTypeChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '9px' }}>
                          {jiraIssueTypeChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Sprint Progress */}
                {sprintChartData.length > 0 && (
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faChartBar} className="h-3.5 w-3.5 text-blue-400" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Sprint Progress</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180} minWidth={0}>
                      <BarChart data={sprintChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }}
                          labelFormatter={(label: string) => { const item = sprintChartData.find(d => d.name === label); return item?.fullName || label; }} />
                        <Legend wrapperStyle={{ fontSize: '11px', color: isDark ? 'rgba(255,255,255,0.6)' : '#6b7280' }} />
                        <Bar dataKey="completed" name="Completed" fill="#10b981" stackId="sprint" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="remaining" name="Remaining" fill="#f59e0b" stackId="sprint" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Waterfall Timeline */}
        {jiraWaterfallEvents.length > 0 && (
          <WaterfallView
            events={jiraWaterfallEvents}
            title="Jira Waterfall Timeline"
            onEventClick={(evt) => {
              // Switch to group-by-ticket mode so JiraIssueDetail always renders
              setGroupByTicket(true);
              // Expand the ticket
              setExpandedTickets(prev => {
                const next = new Set(prev);
                next.add(evt.issueKey);
                return next;
              });
              // Also expand the date group and select the event (for when user toggles back)
              const match = feed?.events.find(e => e.issueKey === evt.issueKey);
              if (match) {
                const date = toLocalDateStr(new Date(match.timestamp));
                setExpandedDates(prev => {
                  const next = new Set(prev);
                  next.add(date);
                  return next;
                });
                setSelectedEventId(match.id);
              }
              // Scroll to the ticket group after DOM updates
              setTimeout(() => {
                const el = document.getElementById(`jira-ticket-${evt.issueKey}`);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 150);
            }}
          />
        )}

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {feed && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left sidebar: Stats & Filters */}
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Summary</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-gray-900 dark:text-white">{feed.totalEvents}</div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500">Total Events</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-gray-900 dark:text-white">{feed.developers.length}</div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500">Developers</div>
                  </div>
                </div>
              </div>

              {/* Group by ticket toggle */}
              <button onClick={() => { setGroupByTicket(v => !v); setExpandedTickets(new Set()); }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${groupByTicket ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-700 dark:text-cyan-300' : 'bg-white/90 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700/50 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'}`}>
                <span className="flex items-center gap-1.5"><FontAwesomeIcon icon={faLayerGroup} className="h-3.5 w-3.5" /> Group by Ticket</span>
                <span className="text-[10px]">{groupByTicket ? 'ON' : 'OFF'}</span>
              </button>


              {/* Developer filter */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5" /> Developers
                </h3>
                <button onClick={() => setFilterAuthor('')}
                  className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${!filterAuthor ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50'}`}>
                  All developers ({feed.developers.reduce((s, d) => s + d.issueCount, 0)})
                </button>
                {feed.developers.map((dev) => (
                  <button key={dev.accountId} onClick={() => setFilterAuthor(dev.displayName)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between ${filterAuthor === dev.displayName ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50'}`}>
                    <span className="truncate">{dev.displayName}</span>
                    <span className="text-[10px] text-slate-500 ml-2 flex-shrink-0">{dev.issueCount}</span>
                  </button>
                ))}
              </div>

              {/* Status breakdown */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Status Breakdown</h3>
                {filterStatus && (
                  <button onClick={() => setFilterStatus('')} className="w-full text-left px-2 py-1 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear status filter</button>
                )}
                {(() => {
                  return [...feed.statusBreakdown]
                    .sort((a, b) => getStatusSortOrder(a.status) - getStatusSortOrder(b.status))
                    .map((s) => (
                      <button key={s.status} onClick={() => setFilterStatus(filterStatus === s.status ? '' : s.status)}
                        className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-1 py-0.5 transition-colors ${filterStatus === s.status ? 'bg-blue-500/20 ring-1 ring-blue-500/40' : 'hover:bg-gray-100 dark:hover:bg-slate-700/30'}`}>
                        <span className={`px-2 py-0.5 rounded border ${getStatusColor(s.status)}`}>{s.status}</span>
                        <span className="text-slate-500">{s.count}</span>
                      </button>
                    ));
                })()}
              </div>

              {/* Labels breakdown */}
              {(() => {
                const labelCounts = new Map<string, number>();
                for (const e of feed.events) {
                  if (e.labels) {
                    for (const label of e.labels) {
                      labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
                    }
                  }
                }
                if (labelCounts.size === 0) return null;
                const sorted = Array.from(labelCounts.entries()).sort((a, b) => b[1] - a[1]);
                const visibleLabels = showAllLabels ? sorted : sorted.slice(0, 6);
                return (
                  <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Labels</h3>
                    {filterLabel && (
                      <button onClick={() => setFilterLabel('')} className="w-full text-left px-2 py-1 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear label filter</button>
                    )}
                    {visibleLabels.map(([label, count]) => (
                      <button key={label} onClick={() => setFilterLabel(filterLabel === label ? '' : label)}
                        className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1.5 transition-colors ${filterLabel === label ? 'bg-blue-500/20 ring-1 ring-blue-500/40' : 'hover:bg-gray-100 dark:hover:bg-slate-700/30'}`}>
                        <span className={`px-2 py-0.5 rounded border text-[10px] ${filterLabel === label ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' : 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20'}`}>{label}</span>
                        <span className="text-slate-500">{count}</span>
                      </button>
                    ))}
                    {sorted.length > 6 && (
                      <button onClick={() => setShowAllLabels(!showAllLabels)}
                        className="w-full text-center text-[10px] font-medium px-3 py-1 rounded-md bg-gray-200/60 dark:bg-slate-700/60 text-gray-600 dark:text-slate-400 hover:bg-gray-300/70 dark:hover:bg-slate-600/70 transition-colors">
                        {showAllLabels ? 'Show less' : `Show all ${sorted.length} labels`}
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Issue types */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Issue Types</h3>
                {filterIssueType && (
                  <button onClick={() => setFilterIssueType('')} className="w-full text-left px-2 py-1 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear type filter</button>
                )}
                {feed.issueTypeBreakdown.map((t) => (
                  <button key={t.type} onClick={() => setFilterIssueType(filterIssueType === t.type ? '' : t.type)}
                    className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1.5 transition-colors ${filterIssueType === t.type ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500/40' : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/30 hover:text-gray-900 dark:hover:text-white'}`}>
                    <span>{t.type}</span>
                    <span className="text-slate-500">{t.count}</span>
                  </button>
                ))}
              </div>

              {/* Sprint summary */}
              {feed.sprintSummary.length > 0 && (
                <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Sprints</h3>
                  {filterSprint && (
                    <button onClick={() => setFilterSprint('')} className="w-full text-left px-2 py-1 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear sprint filter</button>
                  )}
                  {(() => {
                    const active = feed.sprintSummary.filter(s => s.state === 'active' || s.state === 'future');
                    const closed = feed.sprintSummary.filter(s => s.state !== 'active' && s.state !== 'future').slice(-3).reverse();
                    return [...active, ...closed];
                  })().map((sprint) => (
                    <button key={sprint.name} onClick={() => setFilterSprint(filterSprint === sprint.name ? '' : sprint.name)}
                      className={`w-full text-left bg-gray-50 dark:bg-slate-900/50 rounded-lg p-2.5 cursor-pointer transition-colors ${filterSprint === sprint.name ? 'ring-1 ring-blue-500/40 bg-blue-500/10' : 'hover:bg-gray-100 dark:hover:bg-slate-800/70'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-900 dark:text-white font-medium truncate">{sprint.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${sprint.state === 'active' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : sprint.state === 'future' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'bg-slate-200 dark:bg-slate-600/30 text-slate-600 dark:text-slate-400'}`}>{sprint.state}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">{sprint.completedIssues}/{sprint.totalIssues} completed</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Event type toggles */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-2">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  <FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5 inline mr-1" /> Show/Hide
                </h3>
                {[
                  { label: 'Status Changes', state: showStatusChanges, set: setShowStatusChanges },
                  { label: 'Assignments', state: showAssignments, set: setShowAssignments },
                  { label: 'Created', state: showCreated, set: setShowCreated },
                  { label: 'Sprint Changes', state: showSprintChanges, set: setShowSprintChanges },
                  { label: 'Comments', state: showComments, set: setShowComments },
                ].map(({ label, state, set }) => (
                  <button key={label} onClick={() => set(!state)}
                    className="w-full flex items-center justify-between px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors">
                    <span className={state ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-slate-500 line-through'}>{label}</span>
                    {state ? <FontAwesomeIcon icon={faEye} className="h-3 w-3 text-blue-400" /> : <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3 text-slate-600" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Timeline */}
            <div className="lg:col-span-3 space-y-2">
              {filteredEvents.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="h-6 w-6 text-gray-400 dark:text-slate-600" />
                  </div>
                  <p className="text-sm text-gray-400 dark:text-slate-500">No events match your filters</p>
                </div>
              )}

              {/* ===== Grouped by Ticket view ===== */}
              {groupByTicket && ticketGroups.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-1 mb-2">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">
                      {ticketGroups.length} ticket{ticketGroups.length !== 1 ? 's' : ''} &middot; {filteredEvents.length} events
                    </span>
                  </div>
                  {ticketGroups.map((group) => {
                    const isTicketExpanded = expandedTickets.has(group.issueKey);
                    const eventTypes = [...new Set(group.events.map(e => e.type))];
                    const authors = [...new Set(group.events.map(e => e.author.displayName))];
                    return (
                      <div key={group.issueKey} id={`jira-ticket-${group.issueKey}`} className="bg-white/60 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/30 rounded-xl overflow-hidden">
                        <button onClick={() => toggleTicket(group.issueKey)}
                          className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-slate-700/10 transition-colors text-left">
                          <div className="mt-0.5 flex-shrink-0 flex items-center gap-1 text-blue-400">
                            {isTicketExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-slate-500" />}
                            {group.issueTypeIcon && <img src={group.issueTypeIcon} alt="" className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            {group.parent && (
                              <div className="flex items-center gap-1 mb-0.5">
                                <span className="text-[10px] text-slate-500">{group.parent.issueType || 'Parent'}:</span>
                                <span className="text-[10px] font-mono text-slate-500">{group.parent.key}</span>
                                <span className="text-[10px] text-slate-600 truncate max-w-[200px]">{group.parent.summary}</span>
                                <span className="text-[10px] text-slate-600">›</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono font-medium text-blue-400">{group.issueKey}</span>
                              <span className="text-xs text-gray-600 dark:text-slate-300 truncate max-w-[280px]">{group.issueSummary}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${group.issueType === 'Epic' ? 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/20' :
                                  group.issueType === 'Bug' ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/20' :
                                    group.issueType === 'Sub-task' ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/20' :
                                      group.issueType === 'Story' ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/20' :
                                        'bg-slate-200/50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600/30'
                                }`}>{group.issueType}</span>
                              {group.priorityIcon && <img src={group.priorityIcon} alt={group.priority} title={group.priority} className="h-3.5 w-3.5 flex-shrink-0" />}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {group.latestStatus && (
                                <span className={`px-1.5 py-0.5 text-[10px] rounded border ${getStatusColor(group.latestStatus)}`}>{group.latestStatus}</span>
                              )}
                              <span className="text-[10px] text-gray-500 dark:text-slate-500 bg-gray-200 dark:bg-slate-700/50 px-1.5 py-0.5 rounded">{group.events.length} event{group.events.length !== 1 ? 's' : ''}</span>
                              {eventTypes.map(t => {
                                const cfg = EVENT_TYPE_CONFIG[t];
                                if (!cfg) return null;
                                const TypeIcon = cfg.icon;
                                return <span key={t} title={cfg.label}><TypeIcon className={`h-3 w-3 ${cfg.color}`} /></span>;
                              })}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="text-[10px] text-slate-400">{authors.length > 1 ? `${authors.length} people` : authors[0]}</div>
                            <div className="text-[10px] text-slate-500" title={new Date(group.latestTimestamp).toLocaleString()}>
                              {formatTime(group.latestTimestamp)}
                            </div>
                          </div>
                        </button>
                        {isTicketExpanded && (
                          <div className="border-t border-slate-700/20">
                            {group.events
                              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                              .map((event) => {
                                const config = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.field_change;
                                const Icon = config.icon;
                                return (
                                  <div key={event.id} className="flex items-start gap-2 px-4 py-2 border-b border-slate-700/10 last:border-b-0">
                                    <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${config.color}`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] text-gray-600 dark:text-slate-300 capitalize">{event.type.replace(/_/g, ' ')}</span>
                                        {event.type === 'status_change' && event.fromStatus && event.toStatus && (
                                          <>
                                            <span className={`px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(event.fromStatus)}`}>{event.fromStatus}</span>
                                            <span className="text-slate-500 text-[10px]">→</span>
                                            <span className={`px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(event.toStatus)}`}>{event.toStatus}</span>
                                          </>
                                        )}
                                        {event.type === 'assignment' && (
                                          <span className="text-[10px] text-gray-500 dark:text-slate-400">{event.fromAssignee || 'Unassigned'} → <span className="text-purple-700 dark:text-purple-300">{event.toAssignee || 'Unassigned'}</span></span>
                                        )}
                                        {event.type === 'sprint_change' && (
                                          <span className="text-[10px] text-gray-500 dark:text-slate-400">{event.fromSprint && <>{event.fromSprint} → </>}<span className="text-cyan-700 dark:text-cyan-300">{event.toSprint || 'Removed'}</span></span>
                                        )}
                                        {event.type === 'created' && <span className="text-[10px] text-emerald-600 dark:text-emerald-400">Issue created</span>}
                                        {event.type === 'resolved' && <span className="text-[10px] text-green-600 dark:text-green-400">Resolved: {event.toValue}</span>}
                                      </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <div className="text-[10px] text-slate-400">{event.author.displayName}</div>
                                      <div className="text-[9px] text-slate-500">{new Date(event.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} {formatTime(event.timestamp)}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            {/* Inline Jira detail */}
                            <div className="border-t border-slate-700/20">
                              <JiraIssueDetail
                                issueKey={group.issueKey}
                                jiraDomain={jiraDomain}
                                jiraEmail={jiraEmail}
                                jiraApiToken={jiraApiToken}
                                onClose={() => toggleTicket(group.issueKey)}
                                onNavigate={(key) => { setExpandedTickets(prev => { const next = new Set(prev); next.add(key); return next; }); }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ===== Normal date-grouped view ===== */}
              {!groupByTicket && groupedByDate.map(([date, events]) => {
                const isExpanded = expandedDates.has(date);
                const isToday = date === toLocalDateStr();
                const dayLabel = isToday ? 'Today' : formatTimestamp(events[0].timestamp);

                return (
                  <div key={date} className="bg-white/60 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/30 rounded-xl overflow-hidden">
                    {/* Date header */}
                    <button onClick={() => toggleDate(date)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors">
                      <div className="flex items-center gap-2">
                        {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-gray-400 dark:text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-gray-400 dark:text-slate-500" />}
                        <span className={`text-sm font-medium ${isToday ? 'text-blue-400' : 'text-gray-900 dark:text-white'}`}>{dayLabel}</span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-700/50 px-1.5 py-0.5 rounded">{events.length} events</span>
                      </div>
                      <span className="text-[10px] text-slate-600">{date}</span>
                    </button>

                    {/* Events */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 dark:border-slate-700/20">
                        {events.map((event) => {
                          const config = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.field_change;
                          const Icon = config.icon;
                          const isSelected = selectedEventId === event.id;
                          return (
                            <div key={event.id} id={`jira-event-${event.id}`}>
                              <div
                                onClick={() => setSelectedEventId(isSelected ? null : event.id)}
                                className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/10 transition-colors border-b border-gray-100 dark:border-slate-700/10 last:border-b-0 cursor-pointer ${isSelected ? 'bg-blue-500/5' : ''}`}>
                                {/* Expand indicator + Icon */}
                                <div className={`mt-0.5 flex-shrink-0 flex items-center gap-1 ${config.color}`}>
                                  <FontAwesomeIcon icon={faChevronRight} className={`h-3 w-3 text-slate-600 transition-transform ${isSelected ? 'rotate-90 text-blue-400' : ''}`} />
                                  <Icon className="h-4 w-4" />
                                </div>
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Parent breadcrumb */}
                                  {event.parent && (
                                    <div className="flex items-center gap-1 mb-0.5">
                                      <span className="text-[10px] text-slate-500">{event.parent.issueType || 'Parent'}:</span>
                                      <span className="text-[10px] font-mono text-slate-500">{event.parent.key}</span>
                                      <span className="text-[10px] text-slate-600 truncate max-w-[200px]">{event.parent.summary}</span>
                                      <span className="text-[10px] text-slate-600">›</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {event.issueTypeIcon && (
                                      <img src={event.issueTypeIcon} alt="" className="h-4 w-4 flex-shrink-0" />
                                    )}
                                    <span className={`text-xs font-mono font-medium ${isSelected ? 'text-blue-300' : 'text-blue-400'}`}>
                                      {event.issueKey}
                                    </span>
                                    <span className="text-xs text-gray-600 dark:text-slate-300 truncate max-w-[280px]">{event.issueSummary}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${event.issueType === 'Epic' ? 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/20' :
                                        event.issueType === 'Bug' ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/20' :
                                          event.issueType === 'Sub-task' || event.isSubtask ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/20' :
                                            event.issueType === 'Story' ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/20' :
                                              'bg-slate-200/50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600/30'
                                      }`}>{event.issueType}</span>
                                    {event.priorityIcon && (
                                      <img src={event.priorityIcon} alt={event.priority} title={event.priority} className="h-3.5 w-3.5 flex-shrink-0" />
                                    )}
                                  </div>
                                  <div className="mt-1">
                                    {renderEventContent(event)}
                                  </div>
                                </div>
                                {/* Author + Time */}
                                <div className="flex-shrink-0 text-right">
                                  <div className="text-xs text-gray-600 dark:text-slate-300 font-medium">{event.author.displayName}</div>
                                  <div className="text-[10px] text-slate-500" title={new Date(event.timestamp).toLocaleString()}>
                                    {formatTime(event.timestamp)}
                                  </div>
                                </div>
                              </div>
                              {/* Inline accordion detail */}
                              {isSelected && (
                                <JiraIssueDetail
                                  issueKey={event.issueKey}
                                  jiraDomain={jiraDomain}
                                  jiraEmail={jiraEmail}
                                  jiraApiToken={jiraApiToken}
                                  onClose={() => setSelectedEventId(null)}
                                  onNavigate={(key) => { setSelectedEventId(null); setTimeout(() => setSelectedEventId('nav-' + key), 50); }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !feed && !error && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faLayerGroup} className="h-8 w-8 text-gray-400 dark:text-slate-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-600 dark:text-slate-300 mb-2">Jira Activity Timeline</h3>
            <p className="text-sm text-gray-400 dark:text-slate-500 max-w-md mx-auto">
              Select a project and click Fetch Activity to see a complete timeline of all Jira changes — status transitions, assignments, sprint moves, bugs, and more.
            </p>
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-blue-400 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 dark:text-slate-300 mb-2">Fetching Jira activity...</h3>
            <p className="text-sm text-gray-400 dark:text-slate-500">Loading issues, changelogs, and sprint data. This may take a moment for large projects.</p>
          </div>
        )}
      </div>

    </div>
  );
}

export default JiraActivityView;
